import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AboutEcosystem from '../components/AboutEcosystem';
import './AboutPage.css';
import './AboutGraph.css';

const philosophy=[
  ['01','Build Strong Foundations','Success begins with the right systems,processes,and financial infrastructure.','FiLayers'],
  ['02','Plant the Right Seeds','Focus on actions that create future opportunities and sustainable growth.','FiSunrise'],
  ['03','Develop Deep Expertise','Mastery creates confidence,trust,and long-term value.','FiBookOpen'],
  ['04','Trust the Process','Great businesses are built through consistency,discipline,and patience.','FiTrendingUp']
];

const outcomes=[
  ['Financial Clarity','See what is happening in the business and make better decisions with confidence.','FiBarChart2'],
  ['Tax Compliance','Stay prepared,organized,and focused on the future rather than reacting to the past.','FiShield'],
  ['Business Infrastructure','Build the operational foundation required to support meaningful growth.','FiLayers'],
  ['Operational Efficiency','Create better workflows that give teams more time,visibility,and control.','FiActivity'],
  ['Growth Readiness','Prepare the people,processes,and systems needed for the next stage.','FiCompass'],
  ['Long-Term Stability','Develop resilience through disciplined execution and continuous improvement.','FiAnchor']
];

const futureAreas=[
  ['Innovation','Exploring better ways to create value across finance,business,and technology.'],
  ['Scalability','Building frameworks that can grow without losing quality or purpose.'],
  ['Technology','Using practical digital systems to strengthen human capability and decision-making.'],
  ['New Ventures','Developing businesses,products,and partnerships aligned with the larger vision.'],
  ['Future Growth','Creating opportunities that serve entrepreneurs today and the next generation.']
];

function Eyebrow({children}) {
  return <span className="about-premium-eyebrow">{children}</span>;
}

function PhilosophyCard({item,index}) {
  return (
    <motion.article
      className="about-philosophy-card"
      initial={{opacity:0,y:22}}
      whileInView={{opacity:1,y:0}}
      viewport={{once:true,amount:.15}}
      transition={{delay:index*.08}}
      whileHover={{y:-7}}
    >
      <div className="about-card-header">
        <span>{item[0]}</span>
        <SafeIcon icon={FiIcons[item[3]]} />
      </div>
      <h3>{item[1]}</h3>
      <p>{item[2]}</p>
      <SafeIcon icon={FiIcons.FiArrowUpRight} className="about-card-arrow" />
    </motion.article>
  );
}

function OutcomeCard({item,index}) {
  return (
    <motion.article
      className="about-outcome-card"
      initial={{opacity:0,y:18}}
      whileInView={{opacity:1,y:0}}
      viewport={{once:true,amount:.1}}
      transition={{delay:index*.06}}
      whileHover={{y:-6}}
    >
      <div className="about-outcome-icon">
        <SafeIcon icon={FiIcons[item[2]]} />
      </div>
      <span>0{index+1}</span>
      <h3>{item[0]}</h3>
      <p>{item[1]}</p>
    </motion.article>
  );
}

function FutureItem({item,index}) {
  return (
    <motion.div
      className="about-future-item"
      initial={{opacity:0,x:16}}
      whileInView={{opacity:1,x:0}}
      viewport={{once:true}}
      transition={{delay:index*.08}}
    >
      <span>0{index+1}</span>
      <div>
        <h3>{item[0]}</h3>
        <p>{item[1]}</p>
      </div>
      <SafeIcon icon={FiIcons.FiArrowUpRight} />
    </motion.div>
  );
}

function HeroSystemConnections() {
  return (
    <svg
      className="about-system-connections"
      viewBox="0 0 540 540"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="about-connection-gold" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#d4af37" stopOpacity=".9" />
          <stop offset="1" stopColor="#6eaeec" stopOpacity=".45" />
        </linearGradient>
        <linearGradient id="about-connection-blue" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#d4af37" stopOpacity=".82" />
          <stop offset="1" stopColor="#6eaeec" stopOpacity=".52" />
        </linearGradient>
      </defs>
      <line x1="270" y1="270" x2="103" y2="62" stroke="url(#about-connection-gold)" />
      <line x1="270" y1="270" x2="486" y2="115" stroke="url(#about-connection-blue)" />
      <line x1="270" y1="270" x2="484" y2="467" stroke="url(#about-connection-blue)" />
      <line x1="270" y1="270" x2="103" y2="479" stroke="url(#about-connection-gold)" />
      <circle cx="270" cy="270" r="4" />
    </svg>
  );
}

function HeroSystemVisual() {
  return (
    <div className="about-hero-system">
      <div className="about-system-grid" />
      <div className="about-system-orbit about-system-orbit-large" />
      <div className="about-system-orbit about-system-orbit-small" />
      <HeroSystemConnections />
      <motion.div
        className="about-system-core"
        animate={{
          boxShadow:[
            '0 0 0 0 rgba(212,175,55,.2),0 25px 70px rgba(0,0,0,.2)',
            '0 0 0 25px rgba(212,175,55,0),0 25px 70px rgba(0,0,0,.2)',
            '0 0 0 0 rgba(212,175,55,0),0 25px 70px rgba(0,0,0,.2)'
          ]
        }}
        transition={{duration:3.5,repeat:Infinity}}
      >
        <span>PROFITYO</span>
        <strong>GLOBAL SYSTEM</strong>
        <small>ONE VISION · MANY PATHS</small>
      </motion.div>
      <div className="about-system-node about-node-foundations">FOUNDATIONS</div>
      <div className="about-system-node about-node-expertise">EXPERTISE</div>
      <div className="about-system-node about-node-ventures">VENTURES</div>
      <div className="about-system-node about-node-impact">IMPACT</div>
    </div>
  );
}

export default function AboutPage() {
  return (
    <div className="about-premium-page">
      <section className="about-premium-hero">
        <div className="about-hero-image">
          <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1600&q=85" alt="Business leaders collaborating around a table" />
        </div>
        <div className="about-hero-overlay" />
        <div className="container about-premium-hero-layout">
          <motion.div
            className="about-premium-hero-copy"
            initial={{opacity:0,x:-25}}
            animate={{opacity:1,x:0}}
          >
            <Eyebrow>About ProfitYo Global System</Eyebrow>
            <h1>
              Building stronger businesses through
              <em> systems,expertise,and long-term thinking.</em>
            </h1>
            <p>
              ProfitYo Global System is more than a collection of companies. It
              is an interconnected ecosystem of services,solutions,people,and
              innovation working together to help entrepreneurs build,operate,and
              grow with confidence.
            </p>
            <div className="about-premium-actions">
              <Link to="/ecosystem" className="about-premium-button about-button-gold">
                Explore our ecosystem
                <SafeIcon icon={FiIcons.FiArrowUpRight} />
              </Link>
              <a href="#our-story" className="about-premium-button about-button-light">
                Discover our story
              </a>
            </div>
          </motion.div>
          <motion.div
            className="about-premium-hero-visual"
            initial={{opacity:0,scale:.88}}
            animate={{opacity:1,scale:1}}
            transition={{duration:.75}}
          >
            <HeroSystemVisual />
          </motion.div>
        </div>
        <div className="about-premium-scroll">
          <span />
          Scroll to explore
        </div>
      </section>

      <section className="about-story-section" id="our-story">
        <div className="container about-story-layout">
          <div>
            <Eyebrow>Our story</Eyebrow>
            <h2>Why ProfitYo Global System <em>exists.</em></h2>
          </div>
          <div className="about-story-copy">
            <p>
              Many businesses struggle not because they lack ambition,but because
              they lack the systems,infrastructure,and support required for
              sustainable growth.
            </p>
            <p>
              ProfitYo Global System was built to solve that challenge. Our mission
              is to create an ecosystem where entrepreneurs can access the
              expertise,services,and resources needed to build stronger
              foundations,make better decisions,and achieve long-term success.
            </p>
            <p>
              We believe in building infrastructure before scaling,creating value
              before seeking results,improving consistently,and choosing long-term
              thinking over short-term gains.
            </p>
          </div>
        </div>
        <div className="container about-story-statement">
          <span>Our purpose</span>
          <strong>
            Help people build the conditions for durable growth — one stronger
            decision,one stronger system,and one stronger business at a time.
          </strong>
        </div>
      </section>

      <section className="about-philosophy-section">
        <div className="container">
          <div className="about-section-heading">
            <Eyebrow>Our philosophy</Eyebrow>
            <h2>The ProfitYo <em>way.</em></h2>
            <p>
              The principles that shape how we build,collaborate,solve
              problems,and make decisions for the long term.
            </p>
          </div>
          <div className="about-philosophy-grid">
            {philosophy.map((item,index)=>(
              <PhilosophyCard key={item[0]} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="about-ecosystem-section">
        <div className="container about-ecosystem-intro">
          <div>
            <Eyebrow>Our ecosystem</Eyebrow>
            <h2>
              One vision.
              <br />
              <em>Multiple solutions.</em>
            </h2>
          </div>
          <p>
            Each company,venture,and service contributes to a broader mission:
            helping entrepreneurs build stronger businesses through
            systems,expertise,innovation,and long-term thinking.
          </p>
        </div>
        <div className="container about-ecosystem-frame">
          <AboutEcosystem />
        </div>
        <div className="container about-ecosystem-caption">
          <span>Connected by purpose</span>
          <p>
            Shared knowledge,strategic relationships,financial expertise,and
            technology create a system that is greater than any individual
            business within it.
          </p>
          <Link to="/ecosystem" className="about-premium-text-link">
            Explore the full ecosystem
            <SafeIcon icon={FiIcons.FiArrowUpRight} />
          </Link>
        </div>
      </section>

      <section className="about-outcomes-section">
        <div className="container">
          <div className="about-section-heading about-centered-heading">
            <Eyebrow>What we help build</Eyebrow>
            <h2>Helping entrepreneurs build <em>with confidence.</em></h2>
            <p>
              The goal is not simply to provide a service. The goal is to create
              the clarity,capability,and stability that make better growth
              possible.
            </p>
          </div>
          <div className="about-outcomes-grid">
            {outcomes.map((item,index)=>(
              <OutcomeCard key={item[0]} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="about-leadership-section">
        <div className="container about-leadership-layout">
          <div className="about-leadership-image">
            <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1200&q=85" alt="A diverse business team working together" />
            <div className="about-leadership-badge">
              <span>Guided by purpose</span>
              <strong>Integrity · Expertise · Service</strong>
            </div>
          </div>
          <div className="about-leadership-copy">
            <Eyebrow>Leadership & vision</Eyebrow>
            <h2>Guided by <em>purpose.</em></h2>
            <p>
              Our leadership team believes that lasting success is built through
              integrity,expertise,service,and long-term thinking.
            </p>
            <p>
              ProfitYo Global System is committed to creating solutions that help
              businesses thrive today while preparing for tomorrow.
            </p>
            <div className="about-leadership-principles">
              <span><SafeIcon icon={FiIcons.FiCheck} /> Service before self-interest</span>
              <span><SafeIcon icon={FiIcons.FiCheck} /> Expertise that earns trust</span>
              <span><SafeIcon icon={FiIcons.FiCheck} /> Decisions made for the long view</span>
            </div>
          </div>
        </div>
      </section>

      <section className="about-future-section">
        <div className="about-future-glow" />
        <div className="container about-future-layout">
          <div className="about-future-copy">
            <Eyebrow>Innovation & the future</Eyebrow>
            <h2>What we're <em>building next.</em></h2>
            <p>
              ProfitYo Global System continuously invests in new ideas,
              partnerships,technologies,and ventures that create more
              opportunities for entrepreneurs and businesses.
            </p>
            <p className="about-future-highlight">
              What we build today becomes the foundation for tomorrow's growth.
            </p>
          </div>
          <div className="about-future-list">
            {futureAreas.map((item,index)=>(
              <FutureItem key={item[0]} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="about-global-vision-section">
        <div className="container about-global-vision-layout">
          <div>
            <Eyebrow>Global vision</Eyebrow>
            <h2>
              Building an ecosystem for the next generation of
              <em> entrepreneurs.</em>
            </h2>
          </div>
          <div className="about-global-vision-copy">
            <p>
              We envision a future where entrepreneurs have access to the
              systems,expertise,partnerships,and support needed to grow with
              confidence.
            </p>
            <p>
              ProfitYo Global System exists to help make that future possible —
              by creating practical pathways from ambition to stronger businesses
              and lasting value.
            </p>
            <div className="about-premium-actions">
              <Link to="/ecosystem" className="about-premium-button about-button-gold">
                Explore our ecosystem
                <SafeIcon icon={FiIcons.FiArrowUpRight} />
              </Link>
              <Link to="/partnerships" className="about-premium-button about-button-outline-light">
                Partner with us
              </Link>
              <Link to="/contact" className="about-premium-button about-button-outline-light">
                Start your journey
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="about-closing-section">
        <div className="container">
          <Eyebrow>The next chapter</Eyebrow>
          <h2>
            Build stronger.
            <br />
            <em>Grow with purpose.</em>
          </h2>
          <p>
            A trusted ecosystem for the people,businesses,partnerships,and ideas
            that deserve the opportunity to become something lasting.
          </p>
        </div>
      </section>
    </div>
  );
}